import {useContext, useRef, useState} from "react";
import {ToastContext} from "../../Toast";
import {User, useUser} from "../../UserProvider";
import {AccountApi} from "../../account/AccountApi";
import {useMessages} from "../../i18n";
import {renderedFailureMessage, RenderedFailureKey} from "../../utils/renderedFailure";
import {useSingleFlight} from "../../utils/useSingleFlight";

/**
 * Profile-menu inline name editor (ui#139): accounts provisioned by passwordless email-code login
 * (api#58) have no name, and the menu should offer "Add your name" instead of a broken fallback.
 * Logic layer only (epic #26): the save goes through the same {@link AccountApi.updateProfile} the
 * account page uses, and a saved name is published to the shared user state with the account
 * page's clone-then-set pattern (a mutated same-instance set is bailed out by React), reading the
 * user through a ref so a save cannot revert what the session published while it was in flight.
 */

/**
 * The user's actual name, or `null` when the account is nameless. Blank strings are nameless;
 * so is the legacy "null null" serialization (pre api#64 the backend String.format-ed null name
 * parts), which can outlive the fix in a stale localStorage user until the next /account refresh.
 */
export function realName(fullname: string | undefined): string | null {
	const trimmed = (fullname ?? "").trim();
	if (trimmed === "" || trimmed.split(/\s+/).every((part) => part.toLowerCase() === "null")) {
		return null;
	}
	return trimmed;
}

export interface UseInlineName {
	/** The account's display name, or `null` when nameless (drives the "Add your name" affordance). */
	name: string | null;
	editing: boolean;
	/** True while the PUT is in flight (disables the editor's input). */
	busy: boolean;
	start: () => void;
	cancel: () => void;
	/** Trims and saves; blank input just closes the editor. A failure keeps it open and toasts. */
	save: (value: string) => Promise<void>;
}

export function useInlineName(): UseInlineName {
	const [user, setUser] = useUser();
	const messages = useMessages();
	const {showErrorToast} = useContext(ToastContext);
	const [editing, setEditing] = useState(false);
	const [busy, setBusy] = useState(false);

	// Read through a ref so the publish below clones the user as it stands when the PUT ANSWERS, not as
	// it stood when the key was pressed. The session bootstrap can answer during that round trip, and
	// cloning the press-time user put a pre-bootstrap snapshot back on top of it, reverting email trust,
	// plan and billing state with no request and no error (ui#2013).
	const userRef = useRef(user);
	userRef.current = user;

	// The trigger is Enter on an input, so key AUTO-REPEAT fires many keydowns before `busy` can reach
	// the DOM — the disabled input alone never closed that window (ui#360).
	const flight = useSingleFlight();

	const save = (value: string): Promise<void> => flight.run(async () => {
		const trimmed = value.trim();
		if (trimmed === "") {
			setEditing(false);
			return;
		}
		setBusy(true);
		try {
			await AccountApi.updateProfile({fullname: trimmed});
			// Cloned BEFORE the name is set, so nothing is written to the object React holds in state.
			const named = new User(userRef.current);
			named.fullname = trimmed;
			setUser(named);
			setEditing(false);
		} catch (e) {
			showErrorToast(renderedFailureMessage(
				e, RenderedFailureKey.SaveFailed, messages.errors, "account_inline_name_save"));
		} finally {
			setBusy(false);
		}
	});

	return {
		name: realName(user.fullname),
		editing,
		busy,
		start: () => setEditing(true),
		cancel: () => setEditing(false),
		save,
	};
}
